import { Card, CardContent, Grid, Typography, Box } from '@mui/material';
import { Title, useGetOne, Loading, Error } from 'react-admin';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Event as EventIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { VolumeChart } from '../components/charts/VolumeChart';
import { TokenDistribution } from '../components/charts/TokenDistribution';

const formatNumber = (num: number) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(num);
};

export const Dashboard = () => {
  const { data: stats, isLoading, error } = useGetOne(
    'statistics/dashboard',
    { id: 'current' }  // dummy id, API ignores it
  );

  if (isLoading) return <Loading />;
  if (error) return <Error />;
  if (!stats) return null;

  const totalEarnings = (stats.estimated_service_fee || 0) + (stats.pool_fees || 0);

  return (
    <>
      <Title title="Dashboard" />
      <Grid container spacing={3}>
        {/* Stat Cards */}
        <Grid item xs={12} md={3}>
          <StatCard
            title="Total Chats"
            value={stats.total_chats.toString()}
            subtitle={`${stats.active_chats} active`}
            icon={<PeopleIcon sx={{ fontSize: 40 }} />}
            color="#90caf9"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <StatCard
            title="Total Volume"
            value={`$${formatNumber(stats.total_volume_usd)}`}
            subtitle={`${stats.total_users} users`}
            icon={<TrendingUpIcon sx={{ fontSize: 40 }} />}
            color="#f48fb1"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <StatCard
            title="Active Events"
            value={stats.active_events.toString()}
            subtitle={`${stats.total_events} total`}
            icon={<EventIcon sx={{ fontSize: 40 }} />}
            color="#81c784"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <StatCard
            title="Total Earnings"
            value={`$${formatNumber(totalEarnings)}`}
            subtitle={`${stats.total_prizes_paid} prizes paid`}
            icon={<MoneyIcon sx={{ fontSize: 40 }} />}
            color="#ffb74d"
          />
        </Grid>

        {/* Charts */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Volume by Token
              </Typography>
              <VolumeChart data={stats.volume_by_token} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Token Distribution
              </Typography>
              <TokenDistribution data={stats.volume_by_token} />
            </CardContent>
          </Card>
        </Grid>

        {/* Earnings Breakdown */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Earnings Breakdown
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <EarningsItem
                    label="Service Fee"
                    value={stats.estimated_service_fee}
                    description="~1% from betting volume"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <EarningsItem
                    label="Pool Fees"
                    value={stats.pool_fees}
                    description="5% from additional pools"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <EarningsItem
                    label="Total"
                    value={totalEarnings}
                    description="Combined earnings"
                    highlight
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

interface StatCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
}

const StatCard = ({ title, value, subtitle, icon, color }: StatCardProps) => (
  <Card>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="text.secondary" gutterBottom variant="body2">
            {title}
          </Typography>
          <Typography variant="h4" component="div">
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ color, opacity: 0.7 }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

interface EarningsItemProps {
  label: string;
  value: number;
  description: string;
  highlight?: boolean;
}

const EarningsItem = ({ label, value, description, highlight = false }: EarningsItemProps) => (
  <Box>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant={highlight ? "h4" : "h5"}
      sx={{ color: highlight ? '#4caf50' : 'inherit', my: 1 }}
    >
      ${formatNumber(value)}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {description}
    </Typography>
  </Box>
);
